<template>
    <v-hover v-slot="{ hover }" :disabled="$vuetify.breakpoint.xs">
        <v-card
            :class="hover ? 'elevation-12' : 'elevation-0'"
            :href="currentLink"
            class="fill-height d-flex flex-column transition-fast-in-fast-out fl-product-card"
            @click="trackEvent"
        >
            <div :style="imageOrientation === 'auto' ? '' : 'height: fit-content'" class="border-radius-inherit">
                <template v-if="galleryImplementation === null || quickViewEnabled">
                    <v-img
                        :aspect-ratio="imageAspectRatio"
                        contain
                        :src="currentImage"
                        :height="imageHeight"
                        :max-height="imageMaxHeight"
                        class="border-radius-inherit"
                    >
                        <v-overlay v-if="quickViewEnabled" absolute :value="hover">
                            <v-btn depressed color="primary" @click.prevent="quickViewModal = true" @click.stop>{{
                                $t('quickView')
                            }}</v-btn>
                        </v-overlay>
                    </v-img>
                </template>
                <template v-else-if="galleryImplementation === 'hover' && allImages.length > 1">
                    <v-hover v-slot="{ hover }">
                        <v-container class="pa-0" fluid fill-height>
                            <v-carousel :value="Number(hover)" :show-arrows="false" hide-delimiters height="100%">
                                <v-carousel-item v-for="img in allImages" :key="img">
                                    <v-img
                                        :aspect-ratio="imageAspectRatio"
                                        contain
                                        :src="img"
                                        eager
                                        :height="imageHeight"
                                        :max-height="imageMaxHeight"
                                    />
                                </v-carousel-item>
                            </v-carousel>
                        </v-container>
                    </v-hover>
                </template>
                <template v-else-if="galleryImplementation === 'slider' && allImages.length > 1">
                    <v-container class="pa-0" fluid fill-height>
                        <v-carousel hide-delimiter-background show-arrows-on-hover hide-delimiters height="100%">
                            <v-carousel-item v-for="img in allImages" :key="img">
                                <v-img
                                    :aspect-ratio="imageAspectRatio"
                                    contain
                                    :src="img"
                                    eager
                                    :height="imageHeight"
                                    :max-height="imageMaxHeight"
                                />
                            </v-carousel-item>
                            <!--
              We have to define our own previous and next buttons, because there is no way to prevent the
              clicks on these buttons from propagating to the card, so our only chance is top stop
              propagation manually using our own buttons.
              -->
                            <template #prev="{ on }">
                                <v-btn @click.prevent="on.click" @click.stop icon
                                    ><v-icon>{{ mdiChevronLeft }}</v-icon></v-btn
                                >
                            </template>
                            <template #next="{ on }">
                                <v-btn @click.prevent="on.click" @click.stop icon
                                    ><v-icon>{{ mdiChevronRight }}</v-icon></v-btn
                                >
                            </template>
                        </v-carousel>
                    </v-container>
                </template>
            </div>

            <v-dialog width="800" v-if="quickViewEnabled" v-model="quickViewModal">
                <v-card>
                    <v-col class="text-right">
                        <v-btn right icon small @click="quickViewModal = false" @click.stop>
                            <v-icon>{{ mdiClose }}</v-icon>
                        </v-btn>
                    </v-col>
                    <v-row class="pb-12" align="center" no-gutters>
                        <v-col cols="12" sm="6">
                            <template v-if="galleryImplementation === 'hover' && allImages.length > 1">
                                <v-hover v-slot="{ hover }">
                                    <v-container class="pa-0" fluid fill-height>
                                        <v-carousel :value="Number(hover)" :show-arrows="false" hide-delimiters height="100%">
                                            <v-carousel-item v-for="img in allImages" :key="img">
                                                <v-img :aspect-ratio="imageAspectRatio" contain :src="img" eager />
                                            </v-carousel-item>
                                        </v-carousel>
                                    </v-container>
                                </v-hover>
                            </template>

                            <template v-else-if="galleryImplementation === 'slider' && allImages.length > 1">
                                <v-container class="pa-0" fluid fill-height>
                                    <v-carousel hide-delimiter-background show-arrows-on-hover hide-delimiters height="100%">
                                        <v-carousel-item v-for="img in allImages" :key="img">
                                            <v-img :aspect-ratio="imageAspectRatio" contain :src="img" eager />
                                        </v-carousel-item>

                                        <!--
                    We have to define our own previous and next buttons, because there is no way to prevent the
                    clicks on these buttons from propagating to the card, so our only chance is top stop
                    propagation manually using our own buttons.
                      -->
                                        <template #prev="{ on }">
                                            <v-btn @click.prevent="on.click" @click.stop icon
                                                ><v-icon>{{ mdiChevronLeft }}</v-icon></v-btn
                                            >
                                        </template>
                                        <template #next="{ on }">
                                            <v-btn @click.prevent="on.click" @click.stop icon
                                                ><v-icon>{{ mdiChevronRight }}</v-icon></v-btn
                                            >
                                        </template>
                                    </v-carousel>
                                </v-container>
                            </template>

                            <template v-else>
                                <v-img
                                    :aspect-ratio="imageAspectRatio"
                                    contain
                                    :src="currentImage"
                                    :height="imageHeight"
                                    :max-height="imageMaxHeight"
                                />
                            </template>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-card-title>
                                <color-variants v-model="selectedVariant" :variants="variants" :color-map="colorMap" />
                            </v-card-title>

                            <v-card-title class="text-h5">
                                <v-row no-gutters>
                                    <v-col cols="12" class="d-flex">
                                        {{ title }}
                                        <span v-for="badge in badges" :key="badge.trigger.property" class="pl-2">
                                            <v-chip class="px-2" small :color="badge.backgroundColor" text-color="white">
                                                {{ badge.text }}
                                            </v-chip>
                                        </span>
                                    </v-col>
                                </v-row>
                            </v-card-title>

                            <v-card-subtitle>
                                <v-row>
                                    <v-col cols="12" sm="6" class="d-flex flex-column subtitle-1 align-self-end">
                                        <p v-if="vendor">{{ vendor }}</p>
                                        <small v-if="ordernumber">{{ $t('orderNumber') }}: {{ ordernumber }}</small>
                                        <small v-if="deliveryTime" :class="{ 'success--text': immediateDelivery }">{{
                                            deliveryTime
                                        }}</small>
                                        <small v-if="additionalInfoText">{{ additionalInfoText }}</small>
                                    </v-col>
                                    <v-col
                                        cols="12"
                                        sm="6"
                                        class="d-flex flex-column text-right subtitle-1 align-self-end"
                                        v-if="showPrice"
                                    >
                                        <small class="text-decoration-line-through error--text" v-text="formattedOldPrice" />
                                        <span class="fl-product-price font-weight-bold text-h6" v-text="formattedPrice" />
                                        <small v-text="basePrice" />
                                    </v-col>
                                </v-row>
                            </v-card-subtitle>

                            <v-card-text v-if="summary"><div v-html="summary" /></v-card-text>

                            <v-card-text>
                                <v-row class="ma-0 justify-end align-center flex-nowrap">
                                    <v-col class="py-0 pl-0 pr-2" :cols="showCart ? 'auto' : '12'">
                                        <v-btn block color="primary" height="40px" depressed :href="currentLink" @click="trackEvent">
                                            <template>{{ $t('resultCtaButtonText') }}</template>
                                        </v-btn>
                                    </v-col>

                                    <template v-if="showCart">
                                        <v-col v-if="withAmountInput && showAddToCartButton" class="py-0 px-2">
                                            <v-select
                                                v-model="cartAmount"
                                                class="fl-cart-input"
                                                :items="cartAmountList"
                                                :append-icon="mdiChevronDown"
                                                height="28px"
                                                outlined
                                                hide-details
                                                dense
                                                @click.prevent
                                                @click.stop
                                            />
                                        </v-col>
                                        <v-col v-if="showAddToCartButton" class="pa-0" :cols="withAmountInput ? 'auto' : '6'">
                                            <v-btn
                                                :disabled="addingToCart"
                                                block
                                                color="primary"
                                                height="40px"
                                                depressed
                                                @click.prevent="onCartClick"
                                                @click.stop
                                            >
                                                <v-icon v-if="showCartIcon || withAmountInput" :left="!!cartText && !withAmountInput">
                                                    {{ mdiCart }}
                                                </v-icon>
                                                <template v-if="!withAmountInput">
                                                    {{ cartText }}
                                                </template>
                                            </v-btn>
                                        </v-col>
                                    </template>
                                </v-row>
                            </v-card-text>
                        </v-col>
                    </v-row>
                </v-card>
            </v-dialog>

            <div class="d-flex flex-column align-start flex-grow-1">
                <div class="badge-container">
                    <div v-for="badge in badges" :key="badge.trigger.property" class="float-start">
                        <div class="d-flex flex-row pa-2">
                            <v-chip small :color="badge.backgroundColor" text-color="white">{{ badge.text }}</v-chip>
                        </div>
                    </div>
                </div>

                <div class="d-flex pl-4 pr-4 mt-5 justify-space-between align-content-center width-100">
                    <span class="subtitle-2 text-uppercase align-self-end" v-if="vendorStylePosition === 'aboveName'">{{ vendor }}</span>
                    <color-variants v-model="selectedVariant" :variants="variants" :color-map="colorMap" />
                </div>

                <v-card-title
                    v-if="title"
                    class="subtitle-2 font-weight-bold pt-0 align-start flex-grow-1"
                    :class="{ 'pb-0': vendorStylePosition === 'belowName' }"
                    >{{ title }}</v-card-title
                >
                <span class="subtitle-2 text-uppercase ml-4 pb-4" v-if="vendorStylePosition === 'belowName'">{{ vendor }}</span>

                <v-card-text class="text--secondary" v-if="summary"><div v-html="summary" /></v-card-text>

                <!-- align 2 bottom -->
                <v-spacer />

                <v-divider class="width-100" />
                <template>
                    <v-card-text
                        v-if="ordernumber || deliveryTime || additionalInfoText"
                        class="d-flex flex-column pb-0 pt-1 text--secondary fl-meta-information"
                    >
                        <small v-if="ordernumber">{{ $t('orderNumber') }}: {{ ordernumber }}</small>
                        <small v-if="deliveryTime" :class="{ 'success--text': immediateDelivery }">{{ deliveryTime }}</small>
                        <small v-if="additionalInfoText">{{ additionalInfoText }}</small>
                    </v-card-text>
                </template>

                <v-card-actions class="pl-4 width-100">
                    <v-row class="ma-0 mt-2 justify-space-between">
                        <v-col class="d-flex pa-0">
                            <div
                                v-if="showPrice"
                                class="d-flex flex-column justify-end"
                                :class="{ 'pb-1': !basePrice, 'pb-2': !basePrice && withAmountInput }"
                            >
                                <small class="text-decoration-line-through error--text" v-text="formattedOldPrice" />
                                <span class="fl-product-price font-weight-bold text-h6" v-text="formattedPrice" />
                                <small class="text--secondary" v-text="basePrice" />
                            </div>
                        </v-col>
                        <v-col v-if="showCart" class="d-flex pa-0" cols="auto" @click.stop>
                            <v-row class="ma-0 flex-column align-self-end" :style="{ width: withAmountInput ? '70px' : 'auto' }">
                                <v-col v-if="withAmountInput && showAddToCartButton" class="pa-0">
                                    <v-select
                                        v-model="cartAmount"
                                        class="fl-cart-input"
                                        :items="cartAmountList"
                                        :append-icon="mdiChevronDown"
                                        outlined
                                        hide-details
                                        dense
                                        @click.prevent
                                        @click.stop
                                    />
                                </v-col>
                                <v-col class="pa-0">
                                    <v-btn
                                        v-if="showAddToCartButton"
                                        :disabled="addingToCart"
                                        color="primary"
                                        :height="withAmountInput ? '38px' : undefined"
                                        :class="{ 'mt-1': withAmountInput }"
                                        depressed
                                        small
                                        :block="withAmountInput"
                                        @click.prevent="onCartClick"
                                        @click.stop
                                    >
                                        <v-icon v-if="showCartIcon || withAmountInput" :left="!!cartText && !withAmountInput">
                                            {{ mdiCart }}
                                        </v-icon>
                                        <template v-if="!withAmountInput">
                                            {{ cartText }}
                                        </template>
                                    </v-btn>
                                    <v-btn
                                        v-else
                                        depressed
                                        small
                                        color="primary"
                                        :height="withAmountInput ? '38px' : undefined"
                                        :href="currentLink"
                                        @click="trackEvent"
                                    >
                                        <template>{{ $t('resultCtaButtonText') }}</template>
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-card-actions>
                <!--                //TODO re-add sizes once we know how to handle them -->
                <!--                <div v-if="hover && selectedVariant && selectedVariant.sizeItems" class="d-flex flex-column ma-3 mt-0">-->
                <!--                    <span class="ma-1 text-body-2">{{ selectedVariant.sizeItems.label }}</span>-->
                <!--                    <div class="d-flex flex-wrap">-->
                <!--                        <template v-for="item in selectedVariant.sizeItems.items">-->
                <!--                            <v-sheet-->
                <!--                                :key="item.value"-->
                <!--                                class="pa-1 ma-1 d-flex text-caption"-->
                <!--                                style="border: 1px solid #555 !important; cursor: default"-->
                <!--                            >-->
                <!--                                {{ item.value }}-->
                <!--                            </v-sheet>-->
                <!--                        </template>-->
                <!--                    </div>-->
                <!--                </div>-->
            </div>
        </v-card>
    </v-hover>
</template>

<script lang="ts">
import { mdiCart, mdiChevronLeft, mdiChevronRight, mdiHeart, mdiHeartOutline, mdiClose, mdiChevronDown } from '@mdi/js';
import { PropType } from 'vue';
import { Vue } from 'vue-property-decorator';
import ComponentUtils from '../../LayoutAssistedSuggest/services/ComponentUtils';
import { Badge, CloginJsConfig } from '../../../declarations/config';
import { first, range } from 'lodash-es';
import { AddToCartCapable } from '../../Common/Cart/AddToCartCapable';
import { NormalizedVariant } from '../../Common/NormalizedVariant';
import ColorVariants from './ProductCard/ColorVariants.vue';
import { analyticsTracker as flAnalyticsTracker, findologicTracker as flFindologicTracker } from '@findologic/js-common';
import { EventNames } from '../../Common/enums';

export default Vue.extend({
    name: 'ProductCard',
    components: { ColorVariants },
    props: {
        id: {
            type: String,
            required: true,
        },
        title: {
            type: String,
            required: true,
        },
        vendor: {
            type: String,
            required: true,
        },
        image: {
            type: String,
            required: true,
        },
        summary: {
            type: String,
            required: true,
        },
        showPrice: {
            type: Boolean,
            required: true,
        },
        showPriceDecimals: {
            type: Boolean,
            required: true,
        },
        currencySymbol: {
            type: String,
            required: true,
        },
        price: {
            type: Number,
            required: true,
        },
        oldPrice: {
            type: [Number, null],
            default(): null {
                return null;
            },
        },
        basePrice: {
            type: [String, null],
            default(): null {
                return null;
            },
        },
        deliveryTime: {
            type: [String, null],
            default(): null {
                return null;
            },
        },
        immediateDelivery: {
            type: Boolean,
            default: false,
        },
        additionalInfoText: {
            type: [String, null],
            default(): null {
                return null;
            },
        },
        link: {
            type: String,
            required: true,
        },
        ordernumber: {
            type: String,
            required: true,
        },
        showCart: {
            type: Boolean,
            required: true,
        },
        showCartIcon: {
            type: Boolean,
            required: true,
        },
        cartText: {
            type: String,
            required: true,
        },
        withAmountInput: {
            type: Boolean,
            required: true,
        },
        amountInputMin: {
            type: Number,
            required: true,
        },
        amountInputMax: {
            type: Number,
            required: true,
        },
        variants: {
            type: Array as PropType<NormalizedVariant[]>,
            required: true,
        },
        addToCart: {
            type: Function as PropType<AddToCartCapable['addToCart']>,
            default(): undefined {
                return undefined;
            },
        },
        badges: {
            type: Array as PropType<Badge[]>,
            default(): Badge[] {
                return [];
            },
        },
        vendorStylePosition: {
            type: String as PropType<CloginJsConfig['instantFrontend']['productListingPage']['productCard']['vendorStyle']['position']>,
            default: null,
        },
        imageOrientation: {
            type: String as PropType<CloginJsConfig['instantFrontend']['productListingPage']['productCard']['images']['orientation']>,
            required: true,
        },
        galleryImplementation: {
            type: null as PropType<
                CloginJsConfig['instantFrontend']['productListingPage']['productCard']['images']['gallery']['implementation']
            >,
            required: true,
        },
        quickViewEnabled: {
            type: Boolean,
            required: true,
        },
        colorMap: {
            type: undefined as PropType<Record<string, string>>,
            required: true,
        },
        imageHeight: {
            type: String,
            required: true,
        },
        pushRules: {
            type: Array,
            required: true,
        },
        productPlacement: {
            type: String,
            required: true,
        },
        analyticsTracker: {
            type: Object as PropType<flAnalyticsTracker>,
            required: true,
        },
        trigger: {
            type: Object,
            required: true,
        },
        config: {
            type: Object as PropType<CloginJsConfig>,
            required: true,
        },
        operation: {
            type: String,
            required: true,
        },
        requestId: {
            type: String,
            required: true,
        },
        query: {
            type: String,
            required: true,
        },
        resultIndex: {
            type: Number,
            required: true,
        },
    },
    methods: {
        async onCartClick(): Promise<void> {
            this.addingToCart = true;
            try {
                await this.addToCart(this.variants[0], this.cartAmount);
                this.$emit('cart-success');
            } catch (e) {
                this.$emit('cart-error', e);
            } finally {
                this.addingToCart = false;
            }
        },
        formatPrice(price: number): string {
            const isFractionalPrice = price % 1 !== 0;

            return this.componentUtils.formatPrice(
                price,
                window.navigator.language,
                null,
                this.showPriceDecimals || isFractionalPrice ? 2 : 0
            );
        },
        trackEvent() {
            this.analyticsTracker.trackEvent('findologicEvent', 'FINDOLOGIC Result Click', this.query, this.id, null, {
                event: EventNames.RESULT_CLICK,
                ...this.trigger,
                item_id: this.id,
            });

            if (this.productPlacement) {
                this.analyticsTracker.trackEvent('findologicEvent', 'FINDOLOGIC Click', 'Product Placement', this.productPlacement, null, {
                    event: EventNames.PRODUCT_PLACEMENT_CLICK,
                    ...this.trigger,
                    product_placement: this.productPlacement,
                });
            }

            this.pushRules.forEach((bonusRuleName: string) => {
                this.analyticsTracker.trackEvent('findologicEvent', 'FINDOLOGIC Click', 'Bonus Rule', bonusRuleName, null, {
                    event: EventNames.PUSH_RULE_CLICK,
                    ...this.trigger,
                    push_rule: bonusRuleName,
                });
            });

            if (this.config.trackResultsWithFindologic) {
                flFindologicTracker
                    .getInstance(this.config)
                    .trackResultClick(this.requestId, this.id, this.title, this.resultIndex, this.query, this.operation, null);
            }
        },
    },
    computed: {
        formattedPrice(): string {
            return this.formatPrice(this.price);
        },
        formattedOldPrice(): string {
            if (this.oldPrice && this.oldPrice !== this.price) {
                return this.formatPrice(this.oldPrice);
            }

            return '';
        },
        firstVariant(): NormalizedVariant | null {
            if (this.variants.length !== 1) {
                // No clear primary variant - don't consider in stock, so detail button is shown.
                return null;
            }

            return first(this.variants);
        },
        isInStock(): boolean {
            return this.firstVariant && this.firstVariant.isAvailable;
        },
        showAddToCartButton(): boolean {
            return this.variants.length === 1 && this.addToCart && this.isInStock;
        },
        imageAspectRatio(): number | undefined {
            switch (this.imageOrientation) {
                case 'landscape':
                    return 3 / 2;
                case 'portrait':
                    return 2 / 3;
                case 'auto':
                default:
                    return undefined;
            }
        },
        imageMaxHeight(): number | string {
            switch (this.imageOrientation) {
                case 'auto':
                    return 300;
                default:
                    return 'unset';
            }
        },
        allImages(): string[] {
            return this.selectedVariant?.extraImages || [this.image];
        },
        currentImage(): string {
            return this.selectedVariant?.extraImages[0] || this.image;
        },
        currentLink(): string {
            return this.selectedVariant?.url || this.link;
        },
        cartAmountList(): number[] {
            return range(Math.max(1, this.amountInputMin), Math.min(this.amountInputMax, this.firstVariant?.stock || 0) + 1);
        },
    },
    data() {
        return {
            selectedVariant: this.variants?.[0] as NormalizedVariant,
            mdiHeart,
            mdiHeartOutline,
            mdiCart,
            mdiChevronRight,
            mdiChevronLeft,
            mdiChevronDown,
            mdiClose,
            value: true,
            cartAmount: 1,
            showVariant: 0,
            componentUtils: new ComponentUtils(),
            addingToCart: false,
            quickViewModal: false,
        };
    },
});
</script>

<style scoped>
>>> .v-card__text,
.v-card__title {
    word-break: normal;
}

.badge-container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
}

.width-100 {
    width: 100%;
}

.border-radius-inherit {
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
}

.fl-product-price {
    line-height: 1.4rem !important;
}

.fl-meta-information {
    line-height: 1rem;
}
</style>

<style lang="scss" scoped>
.fl-cart-input::v-deep {
    .v-input__slot {
        padding-right: 6px !important;

        .v-select__selections {
            .v-select__selection {
                margin-right: 0px;
            }
        }

        .v-input__append-inner {
            padding-left: 0;
        }
    }
}
</style>
